import { arenaBoundsFor, BOSS_CORE } from './balance'
import type { Boss, Player } from './types'

// DEV opt-in, first acceptance slice: awoken king / feralBite.
// idle.webp: 1441px wide, bbox centred, display scale 186/850.
// Head reaches ~158px forward; rump ~110px rearward, excluding tail.
// Small silhouette margin covers breathing without a moving collision wall.
export const AWOKEN_STANCE_BODY = { front: 170, rear: 115, height: 186 } as const
let enabled = false
export function setStanceBodyTrial(value: boolean): void { enabled = value }

// DEV comparison "beast": the dodge never crosses the beast; a whiffed bite carries
// the beast past the player instead. Same drawn body, no crossing dodge, no slide-out.
let beast = false
export function setBeastBodyTrial(value: boolean): void { beast = value }
export function beastBody(boss: Pick<Boss, 'id'>): boolean {
  return beast && boss.id === 'crowned-king-awoken'
}
const BEAST_BITES = new Set(['feralBite', 'feralFrenzy', 'feralFrenzyExtend'])
// The lunge keeps its speed and dies out linearly; not a warp, not a chase.
export const WHIFF_CARRY = { maxPx: 260 } as const

export function stanceBody(boss: Pick<Boss, 'id'>) {
  return (enabled || beast) && boss.id === 'crowned-king-awoken' ? AWOKEN_STANCE_BODY : undefined
}
export function stanceBodyGap(boss: Boss, side: number): number | undefined {
  const body = stanceBody(boss)
  if (!body) return undefined
  const forward = boss.facing === 'right' ? 1 : -1
  return (side * forward >= 0 ? body.front : body.rear) + BOSS_CORE.playerHalfWidth
}
export function stanceBodyBounds(boss: Boss) {
  const body = stanceBody(boss)
  if (!body) return undefined
  return { left: boss.x - (boss.facing === 'left' ? body.front : body.rear),
    right: boss.x + (boss.facing === 'right' ? body.front : body.rear), height: body.height }
}
export function stanceBiteReach(attackId: string): number | undefined {
  // Teeth extend past the resting head. Keep damage/timing unchanged.
  if (beast && BEAST_BITES.has(attackId)) return AWOKEN_STANCE_BODY.front + 10
  return enabled && attackId === 'feralBite' ? AWOKEN_STANCE_BODY.front + 10 : undefined
}

// Commit once on dodge entry; never chase a moving boss or snap on exit.
// Rejected by the user for the crossing speed; kept only for the stanceBody comparison.
export function stanceDodgeLanding(fromX: number, dir: number, boss: Boss): number | undefined {
  const body = stanceBody(boss)
  if (!body || dir === 0 || beastBody(boss)) return undefined
  const dx = fromX - boss.x
  const gap = stanceBodyGap(boss, dx)!
  if (dx * dir >= 0 || Math.abs(dx) > gap + 24) return undefined
  // The boss may turn after the crossing. Leave room for either orientation.
  const exitGap = Math.max(body.front, body.rear) + BOSS_CORE.playerHalfWidth + 8
  const target = boss.x + dir * exitGap
  const walls = arenaBoundsFor(boss.id)
  if (target >= walls.leftWall && target <= walls.rightWall) return target
  // No exit at the wall: stop on this side, without reversing the input.
  const entry = boss.x - dir * gap
  return (entry - fromX) * dir >= 0 ? entry : fromX
}

// Planned once when a bite enters recovery. If the player is under the head or
// already behind, the beast's momentum carries it until the rump clears the player.
// A player still in front (retreated, or bitten and knocked back) gets no carry.
export function beastWhiffCarry(boss: Boss, player: Player): Boss['whiffCarry'] | undefined {
  const body = stanceBody(boss)
  if (!body || !beastBody(boss) || !boss.currentAttack || !BEAST_BITES.has(boss.currentAttack.id)) return undefined
  if (boss.attackConnected) return undefined   // 噛みついた (受けられた) なら牙を当てた姿勢で止まる
  const dir = boss.facing === 'right' ? 1 : -1
  const rel = (player.x - boss.x) * dir
  if (rel >= body.front + BOSS_CORE.playerHalfWidth) return undefined
  const release = player.x + dir * (body.rear + BOSS_CORE.playerHalfWidth)
  const carry = Math.min(WHIFF_CARRY.maxPx, (release - boss.x) * dir)
  if (carry <= 0) return undefined
  const walls = arenaBoundsFor(boss.id)
  const targetX = Math.max(walls.leftWall, Math.min(walls.rightWall, boss.x + dir * carry))
  const distance = Math.abs(targetX - boss.x)
  if (distance < 0.5) return undefined
  // Speed is continuous with the lunge: start at lunge speed, decelerate to rest.
  const atk = boss.currentAttack
  const lungeSpeed = (atk.lungeDistance ?? 150) / atk.activeMs * 1000
  return { fromX: boss.x, targetX, durationMs: 2 * distance / lungeSpeed * 1000 }
}
// Linear deceleration from the lunge speed to rest. Progress in [0,1].
export function whiffCarryProgress(elapsedMs: number, durationMs: number): number {
  const t = Math.max(0, Math.min(1, elapsedMs / durationMs))
  return 1 - (1 - t) * (1 - t)
}
